import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faArrowUTurnUpLeft } from '@fortawesome/pro-solid-svg-icons/faArrowUTurnUpLeft';
import { faObjectUnion } from '@fortawesome/sharp-solid-svg-icons/faObjectUnion';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { Tooltip } from 'ui-components/components/tooltip';
import { ModalCloseButton } from 'ui-components/components/modal/modal-close-button';
import { emptyObject } from 'ui-components/default-props/empty';
import { ShipmentStatusPropType } from '../../prop-types/shipment-status';
import ShipmentStatus from '../../components/shipment-status';
import ShipmentStatusLabel from '../shipment-status-label';
import { ShipmentStatusLabelContainer } from '../../containers/status-label/shipment';
import { ReturnShipmentPropType } from '../../prop-types/return-shipment';
import Global from '../../store/global-selectors';
import { USD } from '../../constants/currency-codes';

import style from './shipment-status-header.module.less';

const messages = defineMessages({
  tooltipText: {
    description: 'shipmentStatusHeader.autogeneratedTooltip',
    defaultMessage: 'This shipment was created because you printed a packing slip',
    id: 's1MVKS',
  },
  returnMessage: {
    description: 'shipmentStatusHeader.returnMessage',
    defaultMessage: 'RMA #{rmaNumber}',
    id: 'UfDbz5',
  },
  comboMessage: {
    description: 'shipmentStatusHeader.comboMessage',
    defaultMessage: 'Combo #{fulfillmentPlanId}',
    id: 'Kcd723',
  },
  shipmentMessage: {
    description: 'shipmentStatusHeader.shipmentMessage',
    defaultMessage: 'Shipment #{fulfillmentPlanId}',
    id: 'FfnPQj',
  },
  autogeneratedMessage: {
    description: 'shipmentStatusHeader.autogeneratedMessage',
    defaultMessage: 'Auto-generated Shipment',
    id: 'DUUuBt',
  },
  dateShipped: {
    description: 'shipmentStatusHeader.dateShipped',
    defaultMessage: 'Date Shipped',
    id: 'FngV/I',
  },
  markedShipped: {
    description: 'shipmentStatusHeader.markedShipped',
    defaultMessage: 'Manually Marked as Shipped',
    id: 'iisIAc',
  },
  outstanding: {
    description: 'shipmentStatusHeader.outstanding',
    defaultMessage: 'Outstanding',
    id: 'ky0FM5',
  },
  received: {
    description: 'shipmentStatusHeader.received',
    defaultMessage: 'Received',
    id: '73DAAe',
  },
});

const renderShipmentNumber = (fulfillmentPlanId, isReturn, isCombo, rmaNumber, intl) => {
  if (isReturn) {
    return (
      <div className={style['return-combo-header']}>
        <span className={style['return-icon']}>
          <FontAwesomeIcon icon={faArrowUTurnUpLeft} />
        </span>
        <span className={style['return-combo-number']}>{intl.formatMessage(messages.returnMessage, { rmaNumber })}</span>
      </div>
    );
  }

  if (isCombo) {
    return (
      <div className={style['return-combo-header']}>
        <span className={style['combo-icon']}>
          <FontAwesomeIcon icon={faObjectUnion} />
        </span>
        <span className={style['return-combo-number']}>{intl.formatMessage(messages.comboMessage, { fulfillmentPlanId })}</span>
      </div>
    );
  }

  return <div className={style['shipment-number']}>{intl.formatMessage(messages.shipmentMessage, { fulfillmentPlanId })}</div>;
};

const renderShipmentStatus = (fulfillmentPlanId, isRefundAssistView) => {
  return <ShipmentStatusLabelContainer fulfillmentPlanId={fulfillmentPlanId} isRefundAssistView={isRefundAssistView} standalone />;
};

const pillStatusArray = ['Outstanding', 'Received'];

const renderReturnStatusPills = (returnStatus) => {
  if (!pillStatusArray.includes(returnStatus)) {
    return null;
  }
  return <ShipmentStatusLabel standalone status={returnStatus.toLowerCase()} />;
};

const renderAutogeneratedInfo = (isAutogenerated, intl) => {
  if (!isAutogenerated) {
    return null;
  }
  return (
    <div className={style.autogenerated}>
      <span>{intl.formatMessage(messages.autogeneratedMessage)}</span>
      <Tooltip position='bottom' text={intl.formatMessage(messages.tooltipText)}>
        <FontAwesomeIcon icon={faInfoCircle} />
      </Tooltip>
    </div>
  );
};

const renderCloseButton = (onClose) => {
  if (!onClose) {
    return null;
  }
  return <ModalCloseButton className={style['close-button']} closeModal={onClose} />;
};

const renderChildren = (children) => {
  if (!children) {
    return null;
  }
  return <div className={style['children-buttons']}>{children}</div>;
};

const renderExpando = (toggleExpando, caretDirection) => {
  if (!toggleExpando) {
    return null;
  }
  const icon = caretDirection === 'caret-up' ? faCaretUp : faCaretDown;
  return (
    <div className={style['expando-trigger']} onClick={toggleExpando}>
      <FontAwesomeIcon icon={icon} />
    </div>
  );
};

const getShipmentStatusProps = (status, actions, fulfillmentPlanId, fulfillmentId, fulfillmentPlan) => {
  const labelCurrencyCode = get(fulfillmentPlan, 'rateSummary.rate.totalCost.code', USD);

  const {
    packingSlipPrintedStatus,
    labelCreatedDateTime,
    labelPrintedStatus,
    labelPrinted,
    packingSlipPrinted,
    marketplaceNotification,
    shipmentEmailNotification,
    deliveryEmailNotification,
  } = status;

  return {
    labelCurrencyCode,
    packingSlipPrintedStatus,
    labelCreatedDateTime,
    labelPrintedStatus,
    marketplaceNotification,
    shipmentEmailNotification,
    deliveryEmailNotification,
    markLabelPrinted: () => actions.labelPrintedAction(fulfillmentPlanId, fulfillmentId, !labelPrinted),
    markPackingSlipPrinted: () => actions.packingSlipPrintAction(fulfillmentPlanId, !packingSlipPrinted),
  };
};

const getReturnStatusProps = (returnShipment) => {
  const labelCreatedDateTime = get(returnShipment, 'createdDateTime');
  const returnEmailInformation = get(returnShipment, 'returnEmailInformation', emptyObject);
  const labelCurrencyCode = get(returnShipment, 'labelCost.code', USD);

  return {
    labelCurrencyCode,
    labelCreatedDateTime,
    returnEmailInformation,
  };
};

const getStatusProps = (status, actions, fulfillmentPlanId, fulfillmentId, isReturn, returnShipment, fulfillmentPlan) => {
  if (isReturn) {
    return getReturnStatusProps(returnShipment);
  }
  return getShipmentStatusProps(status, actions, fulfillmentPlanId, fulfillmentId, fulfillmentPlan);
};

const ShipmentStatusHeader = ({
  shipmentStatus = emptyObject,
  toggleExpando,
  fulfillmentPlanId = '',
  fulfillmentId = '',
  onClose,
  children,
  isReturn = false,
  isCombo = false,
  isAutogenerated = false,
  actions = emptyObject,
  caretDirection = 'caret-down',
  markedComplete = false,
  rmaNumber = null,
  returnShipment = emptyObject,
  isThirdPartyFulfillment = false,
  isRefundAssistView = false,
}) => {
  const intl = useIntl();
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));

  return (
    <div className={style['shipment-header-container']}>
      <div className={style['shipment-header-info']}>
        <div className={style['shipment-title']}>
          {renderShipmentNumber(fulfillmentPlanId, isReturn, isCombo, rmaNumber, intl)}
          <div className={style['shipment-pills']}>
            {!isReturn && renderShipmentStatus(fulfillmentPlanId, isRefundAssistView)}
            {returnShipment && returnShipment.returnStatus ? renderReturnStatusPills(returnShipment.returnStatus) : null}
          </div>
          {markedComplete && <span className={style['marked-as-complete-header']}>{intl.formatMessage(messages.markedShipped)}</span>}
          {renderAutogeneratedInfo(isAutogenerated, intl)}
          {renderCloseButton(onClose)}
        </div>
        <div className={style['shipment-info']}>
          <ShipmentStatus
            {...getStatusProps(shipmentStatus, actions, fulfillmentPlanId, fulfillmentId, isReturn, returnShipment, fulfillmentPlan)}
            isThirdPartyFulfillment={isThirdPartyFulfillment}
          />
          {renderChildren(children)}
        </div>
      </div>
      {renderExpando(toggleExpando, caretDirection)}
    </div>
  );
};

ShipmentStatusHeader.propTypes = {
  shipmentStatus: ShipmentStatusPropType,
  toggleExpando: PropTypes.func,
  fulfillmentPlanId: PropTypes.string,
  fulfillmentId: PropTypes.string,
  onClose: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isReturn: PropTypes.bool,
  isCombo: PropTypes.bool,
  isAutogenerated: PropTypes.bool,
  actions: PropTypes.object,
  caretDirection: PropTypes.oneOf(['caret-up', 'caret-down']),
  markedComplete: PropTypes.bool,
  rmaNumber: PropTypes.string,
  returnShipment: ReturnShipmentPropType,
  isThirdPartyFulfillment: PropTypes.bool,
  isRefundAssistView: PropTypes.bool,
};

export default ShipmentStatusHeader;
